import { smsLength, type TemplateValues } from '@fieldops/shared'
import { useCallback, useRef, useState } from 'react'
import { cn } from '../../lib/cn.js'
import { ComposeShell } from '../compose/ComposeShell.js'
import { MessageBody, type TextRange } from '../compose/MessageBody.js'
import { DraftedBanner, MissingFields } from '../compose/Notices.js'
import { TemplatesMenu } from '../compose/TemplatesMenu.js'
import { initials, withTemplate } from '../compose/text.js'
import { useComposeAssistant, type MessageValue } from '../compose/useComposeAssistant.js'
import { useTextHistory } from '../compose/useTextHistory.js'
import { Icon } from '../Icon.js'

/**
 * A text is written here and then handed to the phone. FieldOps does not
 * send SMS itself; the device does, with the number and the words filled in.
 * The Assistant writes to a text's length and never sends — there is no Send
 * later, because the phone is what sends. Decisions 86 and 95.
 */
export function SmsCompose(props: SmsComposeProps) {
  if (!props.open) return null
  return <SmsWindow {...props} />
}

export interface SmsComposeProps {
  open: boolean
  onClose: () => void
  contactName: string
  phone: string | null
  initialBody?: string
  /** The job it is about, so the Assistant knows what to suggest. */
  jobId?: string | null
  /** The text being answered. */
  replyToMessageId?: string | null
}

function SmsWindow({ onClose, contactName, phone, initialBody, jobId, replyToMessageId }: SmsComposeProps) {
  const history = useTextHistory<MessageValue>({ subject: '', body: initialBody ?? '' })
  const [range, setRange] = useState<TextRange | null>(null)
  const [missing, setMissing] = useState<string[]>([])
  const bodyRef = useRef<HTMLTextAreaElement>(null)
  const firstName = contactName.split(/\s+/)[0] || null

  const noFiles = useCallback(() => undefined, [])
  const assistant = useComposeAssistant({
    target: { channel: 'SMS', jobId, replyToMessageId },
    history,
    recipientFirstName: () => firstName,
    onAttach: noFiles,
    onRange: setRange,
  })
  const data = assistant.data
  const panel = assistant.context.isError || (data && !data.available) ? null : assistant

  const dialable = phone?.replace(/[^\d+]/g, '') ?? ''
  const body = history.value.body.trim()
  const href = dialable && body ? `sms:${dialable}?body=${encodeURIComponent(body)}` : null
  const length = smsLength(history.value.body)
  const limit = length.perPart * Math.max(1, length.parts)
  const writing = assistant.drafting

  const templateValues = (): TemplateValues => ({
    ...(data?.values ?? {}),
    'contact.name': contactName || null,
    'contact.firstName': firstName,
  })

  return (
    <ComposeShell
      channel="SMS"
      title="Text message"
      heading={data?.heading ?? null}
      assistant={panel}
      drop={null}
      onClose={onClose}
      footer={
        <>
          <TemplatesMenu
            channel="SMS"
            hasText={body !== ''}
            onPick={(template, mode) => {
              const result = withTemplate(history.value, range, template, mode, templateValues())
              history.commit({ subject: '', body: result.value.body })
              setMissing(result.missing)
              setRange({ start: result.caret, end: result.caret })
              requestAnimationFrame(() => bodyRef.current?.focus())
            }}
          />
          <div className="ml-auto flex items-center gap-2">
            {href && !writing ? (
              <a
                href={href}
                className="flex h-9 items-center gap-2 rounded-lg bg-brand-600 px-4 text-[14px] font-semibold text-white hover:bg-brand-700"
                onClick={onClose}
              >
                <Icon name="send" className="size-4" />
                Send
              </a>
            ) : (
              <button
                type="button"
                disabled
                className="flex h-9 items-center gap-2 rounded-lg bg-brand-600 px-4 text-[14px] font-semibold text-white opacity-50"
              >
                <Icon name="send" className="size-4" />
                Send
              </button>
            )}
          </div>
        </>
      }
    >
      <div className="flex min-h-11 items-center gap-3 border-b border-ink-100 py-1.5">
        <span className="w-14 shrink-0 text-[13px] text-ink-500">To</span>
        {dialable ? (
          <span className="flex min-w-0 items-center gap-1.5 rounded-full bg-ink-50 py-0.5 pr-3 pl-0.5 text-[13px] ring-1 ring-ink-200 ring-inset">
            <span
              aria-hidden="true"
              className="grid size-6 shrink-0 place-items-center rounded-full bg-ink-200 text-[10px] font-semibold text-ink-700"
            >
              {initials(contactName || phone || '?')}
            </span>
            <span className="truncate font-semibold text-ink-900">{contactName}</span>
            <span className="truncate text-ink-500">{phone}</span>
          </span>
        ) : (
          <span className="text-[13px] text-ink-500">No number on file.</span>
        )}
      </div>

      <div className="flex flex-col gap-3 pt-4">
        <DraftedBanner writing={writing} drafted={assistant.drafted} onUndo={assistant.undoDraft} onRedo={assistant.redoDraft} />
        <MessageBody
          id="sms-compose-body"
          label="SMS draft"
          value={history.value.body}
          textareaRef={bodyRef}
          placeholder={dialable ? `Text ${contactName}.` : 'Add a number before sending a text.'}
          range={range}
          busy={assistant.busy}
          readOnly={!dialable || writing || assistant.busy !== null}
          canRewrite={Boolean(data?.available)}
          onType={(next) => history.type({ subject: '', body: next })}
          onRange={setRange}
          onRewrite={(action, target) => void assistant.rewrite(action, target)}
          onUndo={history.undo}
          onRedo={history.redo}
        />
        <p
          aria-live="polite"
          className={cn('self-end text-[12px] tabular-nums', length.parts > 1 ? 'text-amber-700' : 'text-ink-400')}
        >
          {length.characters} / {limit} · {length.parts || 1} SMS{length.unicode ? ' · emoji or accents use more room' : ''}
        </p>
        <MissingFields missing={missing} onDismiss={() => setMissing([])} />
      </div>
    </ComposeShell>
  )
}
